import { createServerFn } from "@tanstack/react-start";
import { cmsConfigured, payloadClient } from "../cms/client.js";
import { mediaPublicUrl, rewriteLexicalMediaUrls } from "./media-url";

/** Serializable Lexical / JSON tree. */
export type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

export type PostImage = {
  url: string;
  alt: string;
  width?: number;
  height?: number;
};

export type PostAuthor = {
  name: string;
  avatar: PostImage | null;
};

export type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string | null;
  cover: PostImage | null;
  author: PostAuthor | null;
  tags: string[];
  content: Json | null;
};

export type PostList = {
  posts: Post[];
  ok: boolean;
  unconfigured: boolean;
};

const PAGE_SIZE = 24;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function toImage(value: unknown, fallbackAlt: string): PostImage | null {
  const media = asRecord(value);
  const url = mediaPublicUrl(value);
  if (!url) return null;

  return {
    url,
    alt: (media && asString(media.alt)) ?? fallbackAlt,
    width: typeof media?.width === "number" ? media.width : undefined,
    height: typeof media?.height === "number" ? media.height : undefined,
  };
}

function toAuthor(value: unknown): PostAuthor | null {
  const author = asRecord(value);
  const name = author && (asString(author.name) ?? asString(author.fullName));
  if (!name) return null;
  return { name, avatar: toImage(author?.avatar ?? author?.photo, name) };
}

function toTags(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((tag) => asString(asRecord(tag)?.name) ?? asString(tag))
    .filter((tag): tag is string => Boolean(tag));
}

function toPost(value: unknown): Post | null {
  const doc = asRecord(value);
  if (!doc) return null;

  const slug = asString(doc.slug);
  const title = asString(doc.title);
  if (!slug || !title) return null;

  return {
    id: asString(doc.id) ?? slug,
    slug,
    title,
    excerpt: asString(doc.metaDescription) ?? "",
    date:
      asString(doc.date) ??
      asString(doc.publishedAt) ??
      asString(doc.createdAt) ??
      null,
    cover: toImage(doc.cover, title),
    author: toAuthor(doc.author),
    tags: toTags(doc.tags),
    content: (rewriteLexicalMediaUrls(doc.content) as Json | undefined) ?? null,
  };
}

const listPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<PostList> => {
    if (!cmsConfigured()) return { posts: [], ok: false, unconfigured: true };

    try {
      const payload = await payloadClient();
      const result = await payload.find({
        collection: "posts",
        where: { _status: { equals: "published" } },
        depth: 1,
        limit: PAGE_SIZE,
        sort: "-date",
        draft: false,
        overrideAccess: true,
      });

      return {
        posts: result.docs
          .map(toPost)
          .filter((post): post is Post => post !== null),
        ok: true,
        unconfigured: false,
      };
    } catch {
      return { posts: [], ok: false, unconfigured: false };
    }
  },
);

export function fetchPosts(): Promise<PostList> {
  return listPosts();
}

const getPost = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }): Promise<Post | null> => {
    if (!cmsConfigured()) return null;

    try {
      const payload = await payloadClient();
      const result = await payload.find({
        collection: "posts",
        where: { slug: { equals: slug }, _status: { equals: "published" } },
        depth: 1,
        limit: 1,
        draft: false,
        overrideAccess: true,
      });

      return result.docs.length > 0 ? toPost(result.docs[0]) : null;
    } catch {
      return null;
    }
  });

export function fetchPost(slug: string): Promise<Post | null> {
  return getPost({ data: slug });
}

export function moreArticles(
  posts: Post[],
  current: string,
  count = 3,
): Post[] {
  return posts.filter((post) => post.slug !== current).slice(0, count);
}

export function authorFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

export function formatPostDate(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
